<i18n lang="json">
{
  "en": {
    "title": "RV details",
    "showMoreAttributes": "Show more details",
    "hideMoreAttributes": "Show less details",
    "attributes": {
      "location": "Location",
      "rvType": "RV type",
      "class": "Class",
      "length": "Length",
      "sleepingCapacity": "Sleeping capacity",
      "weight": "Weight",
      "hitchWeight": "Hitch Weight",
      "listingCondition": "Listing Condition",
      "mileage": "Mileage",
      "slideOuts": "Slide Out",
      "beds": "Beds",
      "bathrooms": "Bathrooms",
      "fuelCapacity": "Fuel Capacity",
      "generatorHours": "Generator Hours",
      "seatBelts": "Seat Belts",
      "hitchSize": "Hitch Size"
    }
  },
  "fr": {
    "title": "Détails du VR",
    "showMoreAttributes": "Afficher plus de détails",
    "hideMoreAttributes": "Afficher moins de détails",
    "attributes": {
      "location": "Emplacement",
      "rvType": "Type de VR",
      "class": "Classe",
      "length": "Longueur",
      "sleepingCapacity": "Capacité de couchage",
      "weight": "Poids",
      "hitchWeight": "Poids de l'attelage",
      "listingCondition": "État de l'annonce",
      "mileage": "Kilométrage",
      "slideOuts": "Extensions",
      "beds": "Lits",
      "bathrooms": "Salles de bain",
      "fuelCapacity": "Capacité de carburant",
      "generatorHours": "Heures de générateur",
      "seatBelts": "Ceintures de sécurité",
      "hitchSize": "Taille de l'attelage"
    }
  }
}
</i18n>

<i18n lang="yaml" src="~/i18n/trailer-hitch.yml"></i18n>
<i18n lang="yaml" src="~/i18n/rv-type.yml"></i18n>
<i18n src="~/i18n/measurement.json"></i18n>

<template>
  <div>
    <h2 class="typography-subtitle-2 mb-6">{{ t('title') }}</h2>

    <div class="">
      <RvDetailsAttribute
        :name="t('attributes.location')"
        :value="`${rv.location?.city}, ${rv.location?.regionCode}`"
      />
      <RvDetailsAttribute :name="t('attributes.class')" :value="t(`RVType.${rv.class}`)" />
      <RvDetailsAttribute
        v-if="rv.length"
        :name="t('attributes.length')"
        :value="t(`Measurement.${rv.lengthUnit}`, [rv.length])"
      />
      <RvDetailsAttribute :name="t('attributes.sleepingCapacity')" :value="rv.sleepingCapacity ?? undefined" />
      <div v-if="hasMoreAttributes" class="more-attributes" ref="moreAttributes">
        <div class="more-attributes-inner">
          <RvDetailsAttribute
            v-if="rv.mileage"
            :name="t('attributes.mileage')"
            :value="t(`Measurement.${rv.mileageUnit}`, [rv.mileage])"
          />
          <RvDetailsAttribute
            v-if="rv.weight"
            :name="t('attributes.weight')"
            :value="t(`Measurement.${rv.weightUnit}`, [rv.weight])"
          />
          <RvDetailsAttribute
            v-if="rv.hitchWeight"
            :name="t('attributes.hitchWeight')"
            :value="t(`Measurement.${rv.hitchWeightUnit}`, [rv.hitchWeight])"
          />
          <RvDetailsAttribute
            v-if="rv.hitchSize"
            :name="t('attributes.hitchSize')"
            :value="t(`TrailerHitch.${rv.hitchSize}`)"
          />
          <RvDetailsAttribute v-if="rv.slideOuts" :name="t('attributes.slideOuts')" :value="rv.slideOuts" />
          <RvDetailsAttribute v-if="rv.beds" :name="t('attributes.beds')" :value="rv.beds" />
          <RvDetailsAttribute v-if="rv.bathrooms" :name="t('attributes.bathrooms')" :value="rv.bathrooms" />
          <RvDetailsAttribute
            v-if="rv.fuelCapacity"
            :name="t('attributes.fuelCapacity')"
            :value="t(`Measurement.${rv.fuelCapacityUnit}`, [rv.fuelCapacity])"
          />
          <RvDetailsAttribute v-if="rv.seatBelts" :name="t('attributes.seatBelts')" :value="rv.seatBelts" />
        </div>
      </div>

      <FormButton v-if="hasMoreAttributes" variant="link" @click.prevent="toggleAttributesHandler">{{
        attributesExpanded ? t('hideMoreAttributes') : t('showMoreAttributes')
      }}</FormButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Rv } from '~/types/api'

const { t } = useI18n()

const props = defineProps<{
  rv: Rv
}>()

const attributesExpanded = ref(false)
const moreAttributes = ref<HTMLElement>()

const hasMoreAttributes = computed(
  () =>
    props.rv.mileage ||
    props.rv.weight ||
    props.rv.hitchWeight ||
    props.rv.hitchSize ||
    props.rv.slideOuts ||
    props.rv.beds ||
    props.rv.bathrooms ||
    props.rv.fuelCapacity ||
    props.rv.seatBelts
)

const toggleAttributesHandler = () => {
  const classList = moreAttributes.value?.classList
  if (attributesExpanded.value) {
    classList?.remove('show')
  } else {
    classList?.add('show')
  }

  attributesExpanded.value = !attributesExpanded.value
}
</script>

<style scoped lang="scss">
.more-attributes {
  display: grid;
  opacity: 0;
  grid-template-rows: 0fr;
  transition:
    grid-template-rows 0.5s,
    opacity 1s;

  .more-attributes-inner {
    overflow: hidden;
  }

  &.show {
    opacity: 1;
    grid-template-rows: 1fr;
  }
}
</style>
